import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Button,
  Link,
  Drawer,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search,
  Refresh,
  Error as ErrorIcon,
  Close,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
  FileCopy,
} from '@material-ui/icons';
import { useMezzanine } from '../../context/MezzanineContext';
import { getChannelStatus, getAlarmHistory } from '../../utilities/mezzanineApi';
import ChannelThumbnail from './ChannelThumbnail';

const KALEIDOSCOPE_BASE = 'https://kaleidoscope.corp.amazon.com/v2/source-management/csids/';
const DRAWER_WIDTH_DEFAULT = 480;
const DRAWER_WIDTH_MIN = 360;
const DRAWER_WIDTH_MAX = 1200;

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  summaryBox: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  summaryCard: {
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    minWidth: '120px',
    textAlign: 'center',
  },
  controlsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  tableContainer: {
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  parentRow: {
    backgroundColor: '#fff8f0',
    borderBottom: '8px solid #fff',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#fff0e0',
    },
  },
  parentRowSelected: {
    backgroundColor: '#fff0e0',
    borderBottom: '8px solid #fff',
    cursor: 'pointer',
  },
  statusChip: {
    fontWeight: 'bold',
    fontSize: '11px',
  },
  smallStatusChip: {
    fontWeight: 'bold',
    fontSize: '10px',
    height: '20px',
  },
  regionChip: {
    fontWeight: 'bold',
    fontSize: '10px',
    height: '22px',
  },
  smallRegionChip: {
    fontWeight: 'bold',
    fontSize: '9px',
    height: '18px',
  },
  // Identifies which SRT output an alarm or status line belongs to. Kept
  // monochrome so it does not compete with the severity badges beside it, and
  // truncated because output names are long and the row is one line.
  alarmOutputName: {
    flexShrink: 0,
    maxWidth: '190px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '10px',
    fontFamily: 'monospace',
    color: '#555',
    backgroundColor: '#f0f0f0',
    border: '1px solid #e0e0e0',
    borderRadius: '3px',
    padding: '1px 5px',
  },
  drawerPaper: {
    overflow: 'visible',
  },
  drawerResizeHandle: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '6px',
    height: '100%',
    cursor: 'col-resize',
    zIndex: 1300,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(255, 111, 0, 0.2)',
    },
  },
  drawerResizeHandleActive: {
    backgroundColor: 'rgba(255, 111, 0, 0.4)',
  },
  '@keyframes liveMonitorSpin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  spinning: {
    animation: '$liveMonitorSpin 1.2s linear infinite',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #eee',
  },
  drawerBody: {
    padding: theme.spacing(2),
    overflowY: 'auto',
    flex: 1,
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(1),
  },
  csidsBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    marginBottom: theme.spacing(2),
  },
  csidRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 8px',
    backgroundColor: '#fafafa',
    border: '1px solid #eee',
    borderRadius: '4px',
    fontSize: '11px',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  channelListItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 10px',
    border: '1px solid #eee',
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#eee',
    borderRadius: '4px',
    cursor: 'pointer',
    marginBottom: '4px',
    transition: 'background-color 120ms, border-left-color 120ms',
    '&:hover': {
      backgroundColor: '#fafafa',
    },
  },
  channelListItemHibernated: {
    backgroundColor: '#f5f5f5',
    opacity: 0.85,
  },
  channelListItemFailed: {
    backgroundColor: '#ffebee',
  },
  channelMeta: {
    fontSize: '10px',
    color: '#666',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '2px',
  },
  outputLine: {
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 0',
  },
  outputLabel: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#555',
    minWidth: '32px',
  },
  outputValue: {
    fontSize: '11px',
    padding: '2px 8px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    fontFamily: 'monospace',
  },
  errorIcon: {
    color: '#f44336',
    fontSize: '14px',
  },
  eidText: {
    fontSize: '10px',
    color: '#888',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    marginBottom: theme.spacing(1.5),
  },
  emptyState: {
    color: '#999',
    fontStyle: 'italic',
    fontSize: '12px',
    textAlign: 'center',
    padding: theme.spacing(2),
  },
}));

const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
    case 'CREATED':
      return { bg: '#e8f5e9', color: '#2e7d32' };
    case 'DEPLOYING':
      return { bg: '#fff3e0', color: '#e65100' };
    case 'PENDING':
      return { bg: '#e3f2fd', color: '#1565c0' };
    case 'FAILED':
      return { bg: '#ffebee', color: '#c62828' };
    case 'HIBERNATED':
      return { bg: '#eceff1', color: '#546e7a' };
    case 'STOPPED':
      return { bg: '#fafafa', color: '#616161' };
    case 'DELETING':
      return { bg: '#fce4ec', color: '#c2185b' };
    default:
      return { bg: '#fafafa', color: '#616161' };
  }
};

const getRegionColor = (region) => {
  const colors = {
    lhr: { bg: '#e3f2fd', color: '#1565c0' },
    fra: { bg: '#fff3e0', color: '#e65100' },
    pdx: { bg: '#e8f5e9', color: '#2e7d32' },
    iad: { bg: '#f3e5f5', color: '#7b1fa2' },
  };
  return colors[region?.toLowerCase()] || { bg: '#fafafa', color: '#616161' };
};

const getDeploymentStatus = (channels) => {
  const hasErrors = channels.some(
    (ch) => ch.status === 'FAILED' || ch.outputs?.some((o) => o.error_message)
  );
  const nonHibernated = channels.filter((ch) => ch.status !== 'HIBERNATED');
  const hasHibernated = channels.some((ch) => ch.status === 'HIBERNATED');
  const allHibernated = channels.length > 0 && channels.every((ch) => ch.status === 'HIBERNATED');
  const allActive =
    nonHibernated.length > 0 &&
    nonHibernated.every(
      (ch) =>
        ch.status === 'ACTIVE' && ch.outputs?.length > 0 && ch.outputs.every((o) => o.status === 'CREATED')
    );
  const isPartial = hasHibernated && !allHibernated && allActive && !hasErrors;

  if (allActive && !hasHibernated) return 'Active';
  if (hasErrors) return 'Has Errors';
  if (allHibernated) return 'Hibernated';
  if (isPartial) return 'Partial';
  return 'In Progress';
};

const getStatusBadgeStyle = (label) => {
  switch (label) {
    case 'Active':
      return { bg: '#e8f5e9', color: '#2e7d32' };
    case 'Has Errors':
      return { bg: '#ffebee', color: '#c62828' };
    case 'Hibernated':
    case 'Partial':
      return { bg: '#eceff1', color: '#546e7a' };
    default:
      return { bg: '#fff3e0', color: '#e65100' };
  }
};

// Health levels for the per-channel dot in the side panel:
const getHealthLevel = (status, fetchError) => {
  if (fetchError) return 'error';
  if (!status) return 'gray';
  const alarms = status.active_alarms || [];
  const hasCritical = alarms.some(
    (a) => a.severity === 'CRITICAL' || a.severity === 'MAJOR'
  );
  const hasWarning = alarms.some((a) => a.severity === 'WARNING');
  const signalPresent = status.service?.video_input?.signal_present === true;
  const outputs = status.outputs || [];
  const anyOutputConnected =
    outputs.length === 0 || outputs.some((o) => o.socket_status === 'CONNECTED');

  if (hasCritical || !signalPresent || !anyOutputConnected) return 'red';
  if (hasWarning) return 'yellow';
  return 'green';
};

const HEALTH_COLOR = {
  gray: '#bdbdbd',
  green: '#2e7d32',
  yellow: '#e65100',
  red: '#c62828',
  error: '#c62828',
};

const HEALTH_LABEL = {
  gray: 'Status not fetched',
  green: 'Healthy',
  yellow: 'Warnings',
  red: 'Issues detected',
  error: 'Failed to fetch status',
};

const SEVERITY_COLOR = {
  CRITICAL: { bg: '#c62828', color: '#ffffff' },
  MAJOR: { bg: '#e65100', color: '#ffffff' },
  WARNING: { bg: '#f9a825', color: '#ffffff' },
  MINOR: { bg: '#2e7d32', color: '#ffffff' },
};

const formatRelativeTime = (ts) => {
  if (ts == null) return '';
  const num = typeof ts === 'string' ? Number(ts) : ts;
  if (!Number.isFinite(num)) return '';
  const ms = String(num).length <= 10 ? num * 1000 : num;
  const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth}mo ago`;
  return `${Math.floor(diffMonth / 12)}y ago`;
};

const formatAbsoluteTime = (ts) => {
  if (ts == null) return '';
  const num = typeof ts === 'string' ? Number(ts) : ts;
  if (!Number.isFinite(num)) return '';
  const ms = String(num).length <= 10 ? num * 1000 : num;
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  const hh = pad(d.getUTCHours());
  const mm = pad(d.getUTCMinutes());
  const ss = pad(d.getUTCSeconds());
  const dd = pad(d.getUTCDate());
  const mo = pad(d.getUTCMonth() + 1);
  const yy = pad(d.getUTCFullYear() % 100);
  return `${hh}:${mm}:${ss} ${dd}/${mo}/${yy} UTC`;
};

const tsToDate = (ts) => {
  if (ts == null) return null;
  const num = typeof ts === 'string' ? Number(ts) : ts;
  if (!Number.isFinite(num)) return null;
  const ms = String(num).length <= 10 ? num * 1000 : num;
  return new Date(ms);
};

const dayKeyOf = (ts) => {
  const d = tsToDate(ts);
  if (!d) return '';
  return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
};

const formatDayHeader = (ts) => {
  const d = tsToDate(ts);
  if (!d) return '';
  const today = new Date();
  const todayKey = `${today.getUTCFullYear()}-${today.getUTCMonth() + 1}-${today.getUTCDate()}`;
  const yest = new Date(Date.now() - 24 * 3600 * 1000);
  const yestKey = `${yest.getUTCFullYear()}-${yest.getUTCMonth() + 1}-${yest.getUTCDate()}`;
  const k = dayKeyOf(ts);
  if (k === todayKey) return 'Today';
  if (k === yestKey) return 'Yesterday';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCFullYear() % 100)}`;
};

const extractTimeClear = (timeClear) => {
  if (!timeClear) return null;
  if (typeof timeClear === 'string' || typeof timeClear === 'number') return timeClear;
  if (timeClear.value != null) return timeClear.value;
  return null;
};

const computeClearedSeconds = (timeSet, timeClearVal) => {
  if (timeClearVal == null) return null;
  const setNum = typeof timeSet === 'string' ? Number(timeSet) : timeSet;
  const clrNum = typeof timeClearVal === 'string' ? Number(timeClearVal) : timeClearVal;
  if (!Number.isFinite(setNum) || !Number.isFinite(clrNum)) return null;
  const setS = String(setNum).length <= 10 ? setNum : Math.floor(setNum / 1000);
  const clrS = String(clrNum).length <= 10 ? clrNum : Math.floor(clrNum / 1000);
  return Math.max(0, clrS - setS);
};

// A channel can have several SRT outputs, so an alarm needs attributing to one.
// `component` says what `output_name` refers to: for service-level alarms it
// holds the CHANNEL name, which is redundant next to the channel you already
// selected; for output-level alarms it holds the OUTPUT name, which is the bit
// you actually need. Only surface it in the second case.
const getAlarmOutputName = (alarm) =>
  alarm && alarm.component !== 'service' && alarm.output_name ? alarm.output_name : null;

const groupAlarmHistory = (items) => {
  const groups = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    // Keyed on alarmId AND output_name: two SRT outputs raising the same alarm
    // back to back are separate events and must not collapse into one row.
    if (last && last.alarmId === item.alarmId && last.output_name === item.output_name) {
      last.count += 1;
      last.firstTimeSet = item.timeSet;
      last.firstTimeClear = extractTimeClear(item.timeClear);
      last.entries.push(item);
    } else {
      groups.push({
        alarmId: item.alarmId,
        alarmName: item.alarmName,
        alarmDescription: item.alarmDescription || item.alarmName,
        severity: item.severity,
        component: item.component,
        output_name: item.output_name,
        details: item.details,
        lastTimeSet: item.timeSet,
        lastTimeClear: extractTimeClear(item.timeClear),
        firstTimeSet: item.timeSet,
        firstTimeClear: extractTimeClear(item.timeClear),
        count: 1,
        entries: [item],
      });
    }
  }
  return groups;
};

const formatNumber = (n) => {
  if (n == null) return '—';
  if (typeof n === 'string' && /^\d+$/.test(n)) return Number(n).toLocaleString();
  if (typeof n === 'number') return n.toLocaleString();
  return String(n);
};

const formatUptime = (ms) => {
  if (ms == null || isNaN(ms)) return '—';
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const formatVancTime = (val) => {
  if (val == null) return '—';
  const num = Number(val);
  if (!Number.isFinite(num) || num < 0) return 'never';
  return formatUptime(num);
};

const SortableHeader = ({ label, column, sortColumn, sortDirection, onClick, align = 'left' }) => (
  <TableCell align={align}>
    <Box
      display="flex"
      alignItems="center"
      justifyContent={align === 'center' ? 'center' : 'flex-start'}
      style={{ cursor: 'pointer' }}
      onClick={() => onClick(column)}
    >
      <strong>{label}</strong>
      {sortColumn === column && sortDirection === 'asc' && (
        <ArrowUpward style={{ fontSize: '18px', marginLeft: '8px' }} />
      )}
      {sortColumn === column && sortDirection === 'desc' && (
        <ArrowDownward style={{ fontSize: '18px', marginLeft: '8px' }} />
      )}
      {sortColumn !== column && (
        <UnfoldMore style={{ fontSize: '18px', marginLeft: '8px', color: '#999' }} />
      )}
    </Box>
  </TableCell>
);

const ChannelListItem = ({ channel, classes, onClick, liveStatus, liveStatusLoading, liveStatusError }) => {
  const statusColor = getStatusColor(channel.status);
  // Distinct regions and priorities across this channel's SRT outputs. Same
  // values can repeat across outputs on older manually-created channels, so
  // these are de-duplicated for display.
  const srtOutputs = (channel.outputs || []).filter((o) => o.output_type === 'SRT_CALLER');
  const outputRegions = [...new Set(srtOutputs.map((o) => o.region).filter(Boolean))];
  const outputPriorities = [...new Set(srtOutputs.map((o) => o.stream_priority).filter(Boolean))];
  const isHibernated = channel.status === 'HIBERNATED';
  const isFailed = channel.status === 'FAILED';
  const hasError = channel.outputs?.some((o) => o.error_message);
  const showHealthIndicator = !isHibernated && channel.status !== 'PENDING' && channel.status !== 'DEPLOYING';
  const healthLevel = getHealthLevel(liveStatus, liveStatusError);
  const stripColor = !showHealthIndicator
    ? '#eee'
    : liveStatusLoading && !liveStatus
    ? '#bdbdbd'
    : HEALTH_COLOR[healthLevel] || '#eee';
  const stripTitle = !showHealthIndicator
    ? ''
    : liveStatusLoading && !liveStatus
    ? 'Fetching status...'
    : HEALTH_LABEL[healthLevel] || '';

  const itemClass = isFailed
    ? classes.channelListItemFailed
    : isHibernated
    ? classes.channelListItemHibernated
    : '';

  return (
    <Box
      className={`${classes.channelListItem} ${itemClass}`}
      onClick={() => onClick(channel)}
      style={{ borderLeftColor: stripColor }}
      title={stripTitle}
    >
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Box display="flex" alignItems="center" style={{ gap: '6px' }}>
          <strong style={{ fontSize: '11px', wordBreak: 'break-all' }}>
            {channel.channel_name}
          </strong>
          {hasError && (
            <Tooltip
              title={
                channel.outputs.find((o) => o.error_message)?.error_message || 'Output error'
              }
            >
              <ErrorIcon className={classes.errorIcon} />
            </Tooltip>
          )}
        </Box>
        <Box className={classes.channelMeta}>
          {/* Derived from the outputs, not channel.region / channel.stream_priority:
              those are legacy copies and are null on multi-output channels. A
              channel feeding two EVIS ingresses shows both regions. */}
          {outputRegions.length > 0 ? (
            outputRegions.map((region) => {
              const c = getRegionColor(region);
              return (
                <Chip
                  key={region}
                  label={region.toUpperCase()}
                  size="small"
                  className={classes.smallRegionChip}
                  style={{ backgroundColor: c.bg, color: c.color }}
                />
              );
            })
          ) : (
            <Chip
              label="—"
              size="small"
              className={classes.smallRegionChip}
              style={{ backgroundColor: '#fafafa', color: '#999' }}
            />
          )}
          {outputPriorities.length > 0 && (
            <span>{outputPriorities.map((p) => p.toUpperCase()).join('/')}</span>
          )}
          <span>· {channel.tx_chain_name}</span>
          <span>· {channel.device_id}</span>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" style={{ marginLeft: '8px' }}>
        <Chip
          label={channel.status}
          size="small"
          className={classes.smallStatusChip}
          style={{ backgroundColor: statusColor.bg, color: statusColor.color }}
        />
      </Box>
    </Box>
  );
};

const LiveMonitor = () => {
  const classes = useStyles();
  const {
    channels,
    outputs,
    deployments,
    channelsLoading,
    outputsLoading,
    deploymentsLoading,
    channelsError,
    fetchChannels,
    fetchOutputs,
    fetchDeployments,
  } = useMezzanine();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_WIDTH_DEFAULT);
  const [isResizing, setIsResizing] = useState(false);

  React.useEffect(() => {
    if (!isResizing) return undefined;
    const handleMove = (e) => {
      const newWidth = Math.min(
        DRAWER_WIDTH_MAX,
        Math.max(DRAWER_WIDTH_MIN, window.innerWidth - e.clientX)
      );
      setDrawerWidth(newWidth);
    };
    const handleUp = () => setIsResizing(false);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const loading = channelsLoading || outputsLoading || deploymentsLoading;

  const handleRefresh = () => {
    fetchChannels();
    fetchOutputs();
    fetchDeployments();
  };

  const handleSortToggle = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => {
        if (prev === 'asc') return 'desc';
        setSortColumn(null);
        return null;
      });
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(0);
  };

  // Build outputs map
  const outputsByChannelId = useMemo(() => {
    const map = {};
    outputs.forEach((o) => {
      if (!map[o.channel_id]) map[o.channel_id] = [];
      map[o.channel_id].push(o);
    });
    return map;
  }, [outputs]);

  // Enrich channels with outputs
  const enrichedChannels = useMemo(() => {
    return channels.map((ch) => {
      const chOutputs = outputsByChannelId[ch.channel_id] || [];
      return {
        ...ch,
        outputs: chOutputs,
      };
    });
  }, [channels, outputsByChannelId]);

  const deploymentsByName = useMemo(() => {
    const map = {};
    deployments.forEach((d) => {
      map[d.deployment_name] = d;
    });
    return map;
  }, [deployments]);

  // Group by deployment
  const deploymentGroups = useMemo(() => {
    const groups = {};
    enrichedChannels.forEach((ch) => {
      const name = ch.deployment_name;
      if (!groups[name]) {
        const meta = deploymentsByName[name] || {};
        groups[name] = {
          key: name,
          deployment_name: name,
          eid: ch.eid || meta.eid,
          active_csids: meta.active_csids || [],
          channels: [],
        };
      }
      groups[name].channels.push(ch);
    });
    deployments.forEach((d) => {
      if (!groups[d.deployment_name]) {
        groups[d.deployment_name] = {
          key: d.deployment_name,
          deployment_name: d.deployment_name,
          eid: d.eid,
          active_csids: d.active_csids || [],
          channels: [],
        };
      }
    });
    return Object.values(groups);
  }, [enrichedChannels, deploymentsByName, deployments]);

  // Filter
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return deploymentGroups;
    const q = searchQuery.toLowerCase();
    return deploymentGroups.filter((g) => {
      if (g.deployment_name.toLowerCase().includes(q)) return true;
      if (g.eid?.toLowerCase().includes(q)) return true;
      if (g.active_csids?.some((c) => c.toLowerCase().includes(q))) return true;
      return g.channels.some(
        (ch) =>
          ch.channel_name?.toLowerCase().includes(q) ||
          ch.device_id?.toLowerCase().includes(q) ||
          ch.tx_chain_name?.toLowerCase().includes(q) ||
          // region, stream_priority and stream_id are per-output now, so search
          // the outputs rather than the legacy channel copies.
          ch.outputs?.some(
            (o) =>
              o.region?.toLowerCase().includes(q) ||
              o.stream_priority?.toLowerCase().includes(q) ||
              o.stream_id?.toLowerCase().includes(q) ||
              o.output_name?.toLowerCase().includes(q)
          )
      );
    });
  }, [deploymentGroups, searchQuery]);

  // Sort
  const sortedGroups = useMemo(() => {
    if (!sortColumn || !sortDirection) return filteredGroups;
    return [...filteredGroups].sort((a, b) => {
      let valA;
      let valB;
      switch (sortColumn) {
        case 'name':
          valA = a.deployment_name;
          valB = b.deployment_name;
          return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'csids':
          valA = a.active_csids?.length || 0;
          valB = b.active_csids?.length || 0;
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        case 'channels':
          valA = a.channels.length;
          valB = b.channels.length;
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        case 'status':
          valA = getDeploymentStatus(a.channels);
          valB = getDeploymentStatus(b.channels);
          return sortDirection === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        default:
          return 0;
      }
    });
  }, [filteredGroups, sortColumn, sortDirection]);

  // Stats
  const stats = useMemo(() => {
    return {
      activeChannels: enrichedChannels.filter((c) => c.status === 'ACTIVE').length,
      hibernatedChannels: enrichedChannels.filter((c) => c.status === 'HIBERNATED').length,
      failedChannels: enrichedChannels.filter((c) => c.status === 'FAILED').length,
      deployments: deploymentGroups.length,
    };
  }, [enrichedChannels, deploymentGroups]);

  const selectedGroup = useMemo(() => {
    if (!selectedKey) return null;
    return deploymentGroups.find((g) => g.key === selectedKey) || null;
  }, [deploymentGroups, selectedKey]);

  React.useEffect(() => {
    if (drawerOpen && !selectedGroup) {
      setDrawerOpen(false);
      setSelectedKey(null);
    }
  }, [drawerOpen, selectedGroup]);

  const handleRowClick = (group) => {
    setSelectedKey(group.key);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedKey(null);
  };

  if (loading && channels.length === 0) {
    return (
      <Box
        className={classes.container}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress style={{ color: '#ff6f00' }} />
      </Box>
    );
  }

  if (channelsError && channels.length === 0) {
    return (
      <Box className={classes.container}>
        <Paper style={{ padding: '20px', backgroundColor: '#ffebee', textAlign: 'center' }}>
          <ErrorIcon style={{ color: '#c62828', fontSize: '48px' }} />
          <Typography variant="h6" style={{ color: '#c62828', marginTop: '10px' }}>
            Failed to load monitoring data
          </Typography>
          <Typography variant="body2" style={{ color: '#c62828', marginBottom: '15px' }}>
            {channelsError}
          </Typography>
          <IconButton onClick={handleRefresh} style={{ color: '#c62828', border: '2px solid #c62828' }}>
            <Refresh />
          </IconButton>
        </Paper>
      </Box>
    );
  }

  const pagedGroups = sortedGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className={classes.container}>
      {/* Summary Cards */}
      <Box className={classes.summaryBox}>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#e3f2fd' }}>
          <Typography variant="h5" style={{ color: '#1565c0', fontWeight: 'bold' }}>
            {stats.deployments}
          </Typography>
          <Typography variant="caption" style={{ color: '#1565c0' }}>
            Deployments
          </Typography>
        </Paper>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#e8f5e9' }}>
          <Typography variant="h5" style={{ color: '#2e7d32', fontWeight: 'bold' }}>
            {stats.activeChannels}
          </Typography>
          <Typography variant="caption" style={{ color: '#2e7d32' }}>
            Active Channels
          </Typography>
        </Paper>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#eceff1' }}>
          <Typography variant="h5" style={{ color: '#546e7a', fontWeight: 'bold' }}>
            {stats.hibernatedChannels}
          </Typography>
          <Typography variant="caption" style={{ color: '#546e7a' }}>
            Hibernated
          </Typography>
        </Paper>
        <Paper className={classes.summaryCard} style={{ backgroundColor: '#ffebee' }}>
          <Typography variant="h5" style={{ color: '#c62828', fontWeight: 'bold' }}>
            {stats.failedChannels}
          </Typography>
          <Typography variant="caption" style={{ color: '#c62828' }}>
            Failed
          </Typography>
        </Paper>
      </Box>

      {/* Controls */}
      <Box className={classes.controlsRow}>
        <Box display="flex" alignItems="center" style={{ gap: '12px', flex: 1 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by deployment, channel, EID, CSID, device, region..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            style={{ flex: 1, maxWidth: '500px', backgroundColor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#ff6f00' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <IconButton
          onClick={handleRefresh}
          disabled={loading}
          size="small"
          style={{
            color: '#ff6f00',
            border: '1px solid #ff6f00',
            borderRadius: '4px',
            padding: '6px',
          }}
        >
          {loading ? <CircularProgress size={20} style={{ color: '#ff6f00' }} /> : <Refresh fontSize="small" />}
        </IconButton>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#e0e0e0' }}>
              <SortableHeader
                label="Deployment"
                column="name"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onClick={handleSortToggle}
              />
              <SortableHeader
                label="CSIDs"
                column="csids"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onClick={handleSortToggle}
                align="center"
              />
              <SortableHeader
                label="Channels"
                column="channels"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onClick={handleSortToggle}
                align="center"
              />
              <SortableHeader
                label="Status"
                column="status"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onClick={handleSortToggle}
                align="center"
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedGroups.map((group) => {
              const status = getDeploymentStatus(group.channels);
              const statusStyle = getStatusBadgeStyle(status);
              const isSelected = drawerOpen && selectedKey === group.key;
              return (
                <TableRow
                  key={group.key}
                  className={isSelected ? classes.parentRowSelected : classes.parentRow}
                  onClick={() => handleRowClick(group)}
                >
                  <TableCell>
                    <strong>{group.deployment_name}</strong>
                    {group.eid && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ fontSize: '10px', display: 'block' }}
                      >
                        {group.eid}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={group.active_csids?.length || 0}
                      size="small"
                      style={{
                        fontSize: '10px',
                        height: '22px',
                        backgroundColor:
                          group.active_csids?.length > 0 ? '#e3f2fd' : '#fafafa',
                        color: group.active_csids?.length > 0 ? '#1565c0' : '#999',
                        fontWeight: 'bold',
                        minWidth: '28px',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={group.channels.length}
                      size="small"
                      style={{
                        backgroundColor: '#ff6f00',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: '28px',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={status}
                      size="small"
                      className={classes.statusChip}
                      style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {pagedGroups.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  style={{ color: '#999', fontStyle: 'italic', padding: '24px' }}
                >
                  {searchQuery ? 'No results match your search.' : 'No deployments to display.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={sortedGroups.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      {/* Side Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        classes={{ paper: classes.drawerPaper }}
        PaperProps={{ style: { width: drawerWidth } }}
      >
        {drawerOpen && selectedGroup && (
          <Box display="flex" flexDirection="column" height="100%" position="relative">
            <Box
              className={`${classes.drawerResizeHandle} ${isResizing ? classes.drawerResizeHandleActive : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setIsResizing(true);
              }}
            />
            <Box className={classes.drawerHeader}>
              <Box />
              <IconButton size="small" onClick={handleDrawerClose}>
                <Close />
              </IconButton>
            </Box>

            <Box className={classes.drawerBody}>
              <DeploymentPanel group={selectedGroup} classes={classes} />
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

const AlarmHistoryRow = ({ group, classes }) => {
  const sc = SEVERITY_COLOR[group.severity] || SEVERITY_COLOR.MINOR;
  const componentLabel = (group.component || '').replace(/_/g, '-');
  const outputName = getAlarmOutputName(group);
  const description = group.alarmDescription || group.alarmName;

  const lastClearedSec = computeClearedSeconds(group.lastTimeSet, group.lastTimeClear);
  const isActive = group.lastTimeClear == null;

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '11px',
        lineHeight: 1.3,
        paddingTop: '2px',
        paddingBottom: '2px',
      }}
    >
      <span
        style={{
          fontSize: '9px',
          fontWeight: 'bold',
          backgroundColor: sc.bg,
          color: sc.color,
          borderRadius: '3px',
          flexShrink: 0,
          width: '56px',
          height: '14px',
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {group.severity}
      </span>
      <strong style={{ flexShrink: 0 }}>{componentLabel}</strong>
      {outputName && (
        <Tooltip title={`Output: ${outputName}`}>
          <span className={classes.alarmOutputName}>{outputName}</span>
        </Tooltip>
      )}
      <span style={{ color: '#444', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {description}
        {group.details ? ` (${group.details})` : ''}
      </span>
      {group.count > 1 && (
        <span
          style={{
            fontSize: '10px',
            fontWeight: 'bold',
            color: '#555',
            backgroundColor: '#eee',
            padding: '0 6px',
            borderRadius: '3px',
            lineHeight: '14px',
            flexShrink: 0,
          }}
        >
          ×{group.count}
        </span>
      )}
      <Tooltip title={formatAbsoluteTime(group.lastTimeSet)}>
        <span style={{ fontSize: '10px', color: '#888', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          {formatRelativeTime(group.lastTimeSet)} ·{' '}
          {isActive ? (
            <span style={{ color: '#c62828', fontWeight: 'bold' }}>active</span>
          ) : (
            <span>{lastClearedSec}s before clear</span>
          )}
        </span>
      </Tooltip>
    </Box>
  );
};

const AlarmHistorySection = ({ channelId, classes }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('All'); // All | CRITICAL | MAJOR | WARNING | MINOR

  // Reset whenever the channel changes
  React.useEffect(() => {
    setData(null);
    setError(null);
    setSeverityFilter('All');
  }, [channelId]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAlarmHistory(channelId);
      setData(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err?.message || 'Failed to fetch alarm history');
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  // Sort newest-first, filter by severity, then group consecutive identical alarmIds
  const groupedByDay = useMemo(() => {
    if (!data) return [];
    const filtered =
      severityFilter === 'All'
        ? data
        : data.filter((a) => a.severity === severityFilter);
    const sorted = [...filtered].sort((a, b) => Number(b.timeSet) - Number(a.timeSet));
    const groups = groupAlarmHistory(sorted);
    const days = [];
    let currentKey = null;
    let currentBucket = null;
    for (const g of groups) {
      const k = dayKeyOf(g.lastTimeSet);
      if (k !== currentKey) {
        currentKey = k;
        currentBucket = { key: k, label: formatDayHeader(g.lastTimeSet), groups: [] };
        days.push(currentBucket);
      }
      currentBucket.groups.push(g);
    }
    return days;
  }, [data, severityFilter]);

  const totalCount = data ? data.length : 0;
  const filteredCount = useMemo(() => {
    if (!data) return 0;
    return severityFilter === 'All'
      ? data.length
      : data.filter((a) => a.severity === severityFilter).length;
  }, [data, severityFilter]);

  const SEVERITY_OPTIONS = ['All', 'CRITICAL', 'MAJOR', 'WARNING', 'MINOR'];

  return (
    <Box style={{ marginTop: '8px' }}>
      {/* Toggle button — only when nothing has been loaded yet */}
      {data === null && !loading && (
        <Button
          variant="outlined"
          size="small"
          onClick={fetchHistory}
          style={{
            textTransform: 'none',
            fontSize: '11px',
            color: '#ff6f00',
            borderColor: '#ff6f00',
            padding: '2px 10px',
          }}
        >
          View Alarm History
        </Button>
      )}

      {/* Initial fetch loading state (no data yet) */}
      {loading && data === null && (
        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
          <Refresh className={classes.spinning} style={{ fontSize: '14px', color: '#ff6f00' }} />
          <Typography style={{ fontSize: '11px', color: '#888' }}>
            Fetching alarm history...
          </Typography>
        </Box>
      )}

      {error && (
        <Box style={{ padding: '8px 10px', backgroundColor: '#ffebee', border: '1px solid #ffcdd2', borderRadius: '4px', marginTop: '4px' }}>
          <Typography style={{ fontSize: '11px', color: '#c62828' }}>
            Failed to fetch alarm history: {error}
          </Typography>
          <Button
            size="small"
            onClick={fetchHistory}
            style={{ textTransform: 'none', fontSize: '11px', color: '#c62828', marginTop: '4px', padding: '0' }}
          >
            Retry
          </Button>
        </Box>
      )}

      {/* Loaded view (kept rendered during refresh to avoid layout jump) */}
      {data !== null && (
        <Box>
          {/* Header bar with count + filter chips + refresh */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
            <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>
              History ({filteredCount}{severityFilter !== 'All' ? ` of ${totalCount}` : ''})
            </Typography>
            <Box style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {SEVERITY_OPTIONS.map((s) => {
                const active = severityFilter === s;
                const sc = s !== 'All' ? SEVERITY_COLOR[s] : null;
                return (
                  <span
                    key={s}
                    onClick={() => setSeverityFilter(s)}
                    style={{
                      cursor: 'pointer',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      padding: '2px 8px',
                      borderRadius: '3px',
                      border: active ? '1px solid #ff6f00' : '1px solid #ddd',
                      backgroundColor: active && sc ? sc.bg : active ? '#ff6f00' : '#fff',
                      color: active && sc ? sc.color : active ? '#fff' : '#666',
                      userSelect: 'none',
                    }}
                  >
                    {s}
                  </span>
                );
              })}
            </Box>
            <Box style={{ marginLeft: 'auto' }}>
              <Tooltip title="Refresh history">
                <span>
                  <IconButton
                    size="small"
                    onClick={fetchHistory}
                    disabled={loading}
                    style={{
                      backgroundColor: '#ff6f00',
                      color: '#ffffff',
                      borderRadius: '6px',
                      padding: 0,
                      width: '24px',
                      height: '22px',
                    }}
                  >
                    <Refresh
                      className={loading ? classes.spinning : ''}
                      style={{ fontSize: '14px', color: '#ffffff' }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {data.length === 0 ? (
            <Typography style={{ fontSize: '11px', color: '#888', fontStyle: 'italic', padding: '4px 0' }}>
              No alarm history
            </Typography>
          ) : filteredCount === 0 ? (
            <Typography style={{ fontSize: '11px', color: '#888', fontStyle: 'italic', padding: '4px 0' }}>
              No alarms match the current filter
            </Typography>
          ) : (
            <Box style={{ paddingLeft: '8px', opacity: loading ? 0.6 : 1, transition: 'opacity 120ms' }}>
              {groupedByDay.map((day) => (
                <Box key={day.key}>
                  <Typography
                    style={{
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: '#888',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      marginTop: '6px',
                      marginBottom: '2px',
                      borderBottom: '1px solid #eee',
                      paddingBottom: '2px',
                    }}
                  >
                    {day.label}
                  </Typography>
                  {day.groups.map((g, i) => (
                    <AlarmHistoryRow key={`${day.key}-${i}`} group={g} classes={classes} />
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const ChannelDetails = ({ channel, status, loading, error, classes }) => {
  const [statusExpanded, setStatusExpanded] = useState(false);

  if (loading && !status) {
    return (
      <Box style={{ padding: '20px 12px', backgroundColor: '#fafafa', border: '1px solid #eee', borderTop: 'none', borderRadius: '0 0 4px 4px', marginTop: '-4px', marginBottom: '8px', textAlign: 'center' }}>
        <CircularProgress size={20} style={{ color: '#ff6f00' }} />
        <Typography style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
          Fetching live status...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box style={{ padding: '12px', backgroundColor: '#ffebee', border: '1px solid #ffcdd2', borderTop: 'none', borderRadius: '0 0 4px 4px', marginTop: '-4px', marginBottom: '8px' }}>
        <Typography style={{ fontSize: '11px', color: '#c62828' }}>
          Failed to fetch live status: {error}
        </Typography>
      </Box>
    );
  }

  if (!status) {
    return (
      <Box style={{ padding: '12px', backgroundColor: '#fafafa', border: '1px solid #eee', borderTop: 'none', borderRadius: '0 0 4px 4px', marginTop: '-4px', marginBottom: '8px', textAlign: 'center' }}>
        <Typography style={{ fontSize: '11px', color: '#888' }}>
          Live status not available
        </Typography>
      </Box>
    );
  }

  const svc = status.service || {};
  const vIn = svc.video_input || {};
  const vOut = svc.video_output || {};
  const audio = svc.audio_tracks || [];
  const ref = svc.reference_clock || {};
  const alarms = status.active_alarms || [];
  const outs = status.outputs || [];

  return (
    <Box style={{ padding: '10px 12px', backgroundColor: '#fafafa', border: '1px solid #eee', borderTop: 'none', borderRadius: '0 0 4px 4px', marginTop: '-4px', marginBottom: '8px' }}>
      {/* Input thumbnail. Only polls while this panel is open and the input has
          signal; unmounting on collapse is what stops the polling. */}
      <Box style={{ marginBottom: '8px' }}>
        <ChannelThumbnail
          channelId={channel.channel_id}
          signalPresent={Boolean(vIn.signal_present)}
        />
      </Box>

      {/* Status section header (clickable to expand/collapse) */}
      <Box
        onClick={() => setStatusExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          userSelect: 'none',
          marginBottom: statusExpanded ? '8px' : 0,
        }}
      >
        <span style={{ fontSize: '12px', color: '#666', lineHeight: 1 }}>
          {statusExpanded ? '▾' : '▸'}
        </span>
        <span style={{ color: '#666', textTransform: 'uppercase', fontSize: '10px', fontWeight: 'bold', letterSpacing: '0.4px' }}>
          Status
        </span>
        <span style={{ fontSize: '11px', color: '#888' }}>
          ·{' '}
          {ref.locked ? (
            <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>Ref Clock Locked</span>
          ) : (
            <span style={{ color: '#c62828', fontWeight: 'bold' }}>Ref Clock Not locked</span>
          )}
        </span>
      </Box>

      {statusExpanded && (
        <>
          {/* Video */}
          <Typography className={classes.sectionLabel}>Video</Typography>
          <Box style={{ fontSize: '11px', color: '#444', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px', paddingLeft: '8px' }}>
            <Box>
              <Box style={{ color: '#666', fontWeight: 'bold', marginBottom: '2px' }}>Input</Box>
              {!vIn.signal_present ? (
                <Box style={{ paddingLeft: '8px', color: '#c62828', fontWeight: 'bold' }}>No signal</Box>
              ) : (
                <Box style={{ paddingLeft: '8px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <Box style={{ display: 'flex' }}>
                    <span style={{ color: '#666', minWidth: '90px' }}>Mode</span>
                    <span style={{ color: '#222' }}>{vIn.video_mode || '—'}</span>
                  </Box>
                  <Box style={{ display: 'flex' }}>
                    <span style={{ color: '#666', minWidth: '90px' }}>FPS</span>
                    <span style={{ color: '#222' }}>{vIn.frame_rate || '—'}</span>
                  </Box>
                  <Box style={{ display: 'flex' }}>
                    <span style={{ color: '#666', minWidth: '90px' }}>Bit Depth</span>
                    <span style={{ color: '#222' }}>{vIn.bit_depth ?? '—'}</span>
                  </Box>
                  <Box style={{ display: 'flex' }}>
                    <span style={{ color: '#666', minWidth: '90px' }}>Chroma</span>
                    <span style={{ color: '#222' }}>{vIn.chroma || '—'}</span>
                  </Box>
                </Box>
              )}
            </Box>
            <Box>
              <Box style={{ color: '#666', fontWeight: 'bold', marginBottom: '2px' }}>Output</Box>
              {!vOut.running ? (
                <Box style={{ paddingLeft: '8px', color: '#c62828', fontWeight: 'bold' }}>Not running</Box>
              ) : (
                <Box style={{ paddingLeft: '8px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <Box style={{ display: 'flex' }}>
                    <span style={{ color: '#666', minWidth: '90px' }}>Codec</span>
                    <span style={{ color: '#222' }}>{vOut.codec || '—'}</span>
                  </Box>
                  <Box style={{ display: 'flex' }}>
                    <span style={{ color: '#666', minWidth: '90px' }}>Resolution</span>
                    <span style={{ color: '#222' }}>{vOut.resolution || '—'}</span>
                  </Box>
                  <Box style={{ display: 'flex' }}>
                    <span style={{ color: '#666', minWidth: '90px' }}>FPS</span>
                    <span style={{ color: '#222' }}>{vOut.frame_rate || '—'}</span>
                  </Box>
                  <Box style={{ display: 'flex' }}>
                    <span style={{ color: '#666', minWidth: '90px' }}>Bit Depth</span>
                    <span style={{ color: '#222' }}>{vOut.bit_depth ?? '—'}</span>
                  </Box>
                  <Box style={{ display: 'flex' }}>
                    <span style={{ color: '#666', minWidth: '90px' }}>Color</span>
                    <span style={{ color: '#222' }}>{vOut.color_type || '—'}</span>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Audio */}
          <Typography className={classes.sectionLabel}>
            Audio Tracks ({audio.length})
          </Typography>
          <Box style={{ fontSize: '11px', color: '#444', display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '8px', paddingLeft: '8px' }}>
            {audio.length === 0 && <span style={{ color: '#888' }}>—</span>}
            {audio.map((t) => (
              <Box
                key={t.track_index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  borderLeft: `2px solid ${t.running ? '#2e7d32' : '#c62828'}`,
                  paddingLeft: '6px',
                }}
              >
                <span style={{ color: '#666', fontWeight: 'bold', minWidth: '56px' }}>Track {t.track_index}</span>
                <span style={{ color: '#222' }}>
                  {t.output_codec || '—'} · {t.output_channel_mode || '—'} · PID {t.output_pid ?? '—'}
                  {!t.running && <span style={{ color: '#c62828', fontWeight: 'bold' }}> · not running</span>}
                </span>
              </Box>
            ))}
          </Box>

          {/* VANC */}
          {svc.vanc && (
            <>
              <Typography className={classes.sectionLabel}>VANC</Typography>
              <Box style={{ fontSize: '11px', color: '#444', display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '8px', paddingLeft: '8px' }}>
                <div>
                  <span style={{ color: '#666' }}>DPI:</span>{' '}
                  <span style={{ color: '#222' }}>
                    {svc.vanc.dpi_enabled === true ? 'Enabled' : svc.vanc.dpi_enabled === false ? 'Disabled' : '—'}
                    {svc.vanc.dpi_enabled !== false && (
                      <>
                        {' · '}
                        {svc.vanc.dpi_running ? (
                          <span style={{ color: '#2e7d32' }}>running</span>
                        ) : svc.vanc.dpi_running === false ? (
                          <span style={{ color: '#c62828' }}>not running</span>
                        ) : (
                          '—'
                        )}
                      </>
                    )}
                  </span>
                </div>
                <div>
                  <span style={{ color: '#666' }}>SCTE:</span>{' '}
                  <span style={{ color: '#222' }}>
                    last received {formatVancTime(svc.vanc.time_since_last_received)}
                  </span>
                </div>
                {svc.vanc.invalid_packets != null && svc.vanc.invalid_packets > 0 && (
                  <div>
                    <span style={{ color: '#666' }}>Invalid packets:</span>{' '}
                    <span style={{ color: '#c62828', fontWeight: 'bold' }}>{svc.vanc.invalid_packets}</span>
                  </div>
                )}
              </Box>
            </>
          )}

          {/* Outputs */}
          <Typography className={classes.sectionLabel}>
            Outputs ({outs.length})
          </Typography>
          <Box style={{ fontSize: '11px', color: '#444', display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '8px', paddingLeft: '8px' }}>
            {outs.length === 0 && <span style={{ color: '#888' }}>—</span>}
            {outs.map((o) => {
              const connected = o.socket_status === 'CONNECTED';
              return (
                <Box
                  key={o.output_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    borderLeft: `2px solid ${connected ? '#2e7d32' : '#c62828'}`,
                    paddingLeft: '6px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ color: '#666', fontWeight: 'bold', minWidth: '70px' }}>
                    {(o.output_type || '').replace(/_/g, '-').toUpperCase()}
                  </span>
                  {/* Several outputs can share an output_type, so the type alone
                      does not identify which one this row is. */}
                  {o.output_name && (
                    <span className={classes.alarmOutputName}>{o.output_name}</span>
                  )}
                  <span style={{ color: connected ? '#2e7d32' : '#c62828', fontWeight: 'bold' }}>
                    {o.socket_status || 'NOT CONNECTED'}
                  </span>
                  {connected && (
                    <span style={{ color: '#222' }}>
                      · {o.peer_endpoint || '—'} · RTT {o.rtt_ms != null ? `${o.rtt_ms} ms` : '—'} · uptime{' '}
                      {formatUptime(o.uptime_ms)}
                    </span>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}

      <Divider style={{ margin: '8px 0' }} />

      {/* Active Alarms */}
      <Typography className={classes.sectionLabel}>
        Active Alarms ({alarms.length})
      </Typography>
      {alarms.length === 0 ? (
        <Typography style={{ fontSize: '11px', color: '#2e7d32', padding: '2px 0' }}>
          No active alarms
        </Typography>
      ) : (
        <Box style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' }}>
          {alarms.map((a, i) => {
            const sc = SEVERITY_COLOR[a.severity] || SEVERITY_COLOR.MINOR;
            const description = a.raw?.alarmDescription || a.alarm_name;
            const componentLabel = (a.component || '').replace(/_/g, '-');
            const alarmOutputName = getAlarmOutputName(a);
            return (
              <Box
                key={`${a.alarm_id}-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '11px',
                  lineHeight: 1.3,
                }}
              >
                <span
                  style={{
                    fontSize: '9px',
                    fontWeight: 'bold',
                    backgroundColor: sc.bg,
                    color: sc.color,
                    borderRadius: '3px',
                    flexShrink: 0,
                    width: '56px',
                    height: '14px',
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {a.severity}
                </span>
                <strong style={{ flexShrink: 0 }}>{componentLabel}</strong>
                {alarmOutputName && (
                  <Tooltip title={`Output: ${alarmOutputName}`}>
                    <span className={classes.alarmOutputName}>{alarmOutputName}</span>
                  </Tooltip>
                )}
                <span style={{ color: '#444', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {description}
                  {a.details ? ` (${a.details})` : ''}
                </span>
                {a.time_set && (
                  <Tooltip title={formatAbsoluteTime(a.time_set)}>
                    <span style={{ fontSize: '10px', color: '#888', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {formatRelativeTime(a.time_set)}
                    </span>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Alarm History (lazy-loaded on click) */}
      <AlarmHistorySection channelId={channel.channel_id} classes={classes} />
    </Box>
  );
};

const DeploymentPanel = ({ group, classes }) => {
  const [expandedChannelId, setExpandedChannelId] = useState(null);
  const [statusByChannel, setStatusByChannel] = useState({}); // { channelId: status }
  const [loadingByChannel, setLoadingByChannel] = useState({}); // { channelId: bool }
  const [errorByChannel, setErrorByChannel] = useState({}); // { channelId: errorMsg }
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(10); // seconds
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isFetchingAll, setIsFetchingAll] = useState(false);

  // Reset everything when the deployment changes
  React.useEffect(() => {
    setExpandedChannelId(null);
    setStatusByChannel({});
    setLoadingByChannel({});
    setErrorByChannel({});
    setAutoRefresh(false);
    setLastUpdated(null);
  }, [group.key]);

  // Build the list of channel IDs to fetch (skip HIBERNATED, PENDING, DEPLOYING)
  const fetchableChannelIds = useMemo(() => {
    return group.channels
      .filter((ch) => ch.status === 'ACTIVE' || ch.status === 'FAILED')
      .map((ch) => ch.channel_id);
  }, [group.channels]);

  const fetchableKey = fetchableChannelIds.join(',');

  const fetchAllStatuses = useCallback(async () => {
    if (fetchableChannelIds.length === 0) return;
    setIsFetchingAll(true);
    setLoadingByChannel((prev) => {
      const next = { ...prev };
      fetchableChannelIds.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
    const results = await Promise.allSettled(
      fetchableChannelIds.map((id) => getChannelStatus(id))
    );
    const newStatuses = {};
    const newErrors = {};
    const newLoading = {};
    results.forEach((res, i) => {
      const id = fetchableChannelIds[i];
      newLoading[id] = false;
      if (res.status === 'fulfilled') {
        newStatuses[id] = res.value;
        newErrors[id] = null;
      } else {
        newErrors[id] = res.reason?.message || 'Request failed';
      }
    });
    setStatusByChannel((prev) => ({ ...prev, ...newStatuses }));
    setErrorByChannel((prev) => ({ ...prev, ...newErrors }));
    setLoadingByChannel((prev) => ({ ...prev, ...newLoading }));
    setLastUpdated(new Date());
    setIsFetchingAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchableKey]);

  // Auto-fetch on deployment open (when fetchable IDs change)
  React.useEffect(() => {
    if (fetchableChannelIds.length > 0) {
      fetchAllStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchableKey]);

  // Auto-refresh polling
  React.useEffect(() => {
    if (!autoRefresh || fetchableChannelIds.length === 0) return undefined;
    const interval = setInterval(() => {
      fetchAllStatuses();
    }, autoRefreshInterval * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, autoRefreshInterval, fetchableKey]);

  const sortedChannels = useMemo(() => {
    const order = { ACTIVE: 0, DEPLOYING: 1, PENDING: 2, FAILED: 3, HIBERNATED: 4 };
    return [...group.channels].sort((a, b) => {
      const oa = order[a.status] ?? 5;
      const ob = order[b.status] ?? 5;
      if (oa !== ob) return oa - ob;
      return (a.channel_name || '').localeCompare(b.channel_name || '');
    });
  }, [group.channels]);

  const handleChannelClick = (channel) => {
    setExpandedChannelId((prev) => (prev === channel.channel_id ? null : channel.channel_id));
  };

  return (
    <Box>
      <Typography style={{ fontSize: '16px', fontWeight: 'bold', wordBreak: 'break-all' }}>
        {group.deployment_name}
      </Typography>
      {group.eid && <Typography className={classes.eidText}>{group.eid}</Typography>}

      <Typography className={classes.sectionLabel}>
        Active CSIDs ({group.active_csids?.length || 0})
      </Typography>
      <Box className={classes.csidsBox}>
        {group.active_csids && group.active_csids.length > 0 ? (
          group.active_csids.map((csid) => (
            <CopyableCsid key={csid} csid={csid} classes={classes} />
          ))
        ) : (
          <Typography className={classes.emptyState}>No active CSIDs</Typography>
        )}
      </Box>

      <Divider style={{ margin: '12px 0' }} />

      {/* Live Status controls */}
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '8px 10px',
          backgroundColor: '#fafafa',
          border: '1px solid #eee',
          borderRadius: '4px',
          marginBottom: '8px',
        }}
      >
        <Box display="flex" alignItems="center" style={{ gap: '8px', flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                color="default"
              />
            }
            label={<span style={{ fontSize: '11px' }}>Auto refresh</span>}
            style={{ marginRight: 0 }}
          />
          <Select
            value={autoRefreshInterval}
            onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
            disabled={!autoRefresh}
            variant="outlined"
            style={{ fontSize: '11px', height: '28px', minWidth: '70px' }}
            MenuProps={{ PaperProps: { style: { fontSize: '11px' } } }}
          >
            <MenuItem value={5} style={{ fontSize: '11px' }}>5s</MenuItem>
            <MenuItem value={10} style={{ fontSize: '11px' }}>10s</MenuItem>
          </Select>
          {lastUpdated && (
            <Typography style={{ fontSize: '10px', color: '#888' }}>
              Updated {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh statuses now">
          <span>
            <IconButton
              size="small"
              onClick={fetchAllStatuses}
              disabled={fetchableChannelIds.length === 0}
              style={{
                backgroundColor:
                  fetchableChannelIds.length === 0 ? '#ccc' : '#ff6f00',
                color: '#ffffff',
                borderRadius: '8px',
                padding: 0,
                width: '32px',
                height: '28px',
              }}
            >
              <Refresh
                className={isFetchingAll ? classes.spinning : ''}
                style={{ fontSize: '18px', color: '#ffffff' }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Typography className={classes.sectionLabel}>
        Channels ({group.channels.length})
      </Typography>
      <Box>
        {sortedChannels.map((ch) => (
          <React.Fragment key={ch.channel_id}>
            <ChannelListItem
              channel={ch}
              classes={classes}
              onClick={handleChannelClick}
              liveStatus={statusByChannel[ch.channel_id]}
              liveStatusLoading={loadingByChannel[ch.channel_id]}
              liveStatusError={errorByChannel[ch.channel_id]}
            />
            {expandedChannelId === ch.channel_id && (
              <ChannelDetails
                channel={ch}
                status={statusByChannel[ch.channel_id]}
                loading={loadingByChannel[ch.channel_id]}
                error={errorByChannel[ch.channel_id]}
                classes={classes}
              />
            )}
          </React.Fragment>
        ))}
        {sortedChannels.length === 0 && (
          <Typography className={classes.emptyState}>No channels yet</Typography>
        )}
      </Box>
    </Box>
  );
};

const CopyableCsid = ({ csid, classes }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(csid).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box className={classes.csidRow}>
      <Link
        href={`${KALEIDOSCOPE_BASE}${csid}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ flex: 1, wordBreak: 'break-all' }}
      >
        {csid}
      </Link>
      <Tooltip title={copied ? 'Copied!' : 'Copy CSID'}>
        <IconButton size="small" onClick={handleCopy} style={{ padding: '2px' }}>
          <FileCopy style={{ fontSize: '14px', color: copied ? '#2e7d32' : '#888' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default LiveMonitor;
